/**
 * Corrects Bundler/Seal PDP compare-at prices so the crossed-out total is always
 * regularUnitPrice × quantity (not an intermediate bundle-only total).
 * Does not change checkout pricing — display only.
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 60;
  let applying = false;
  let scheduled = null;
  let observer = null;

  /**
   * Formats integer cents with Shopify's money formatter when available.
   * @param {number} cents
   * @returns {string}
   */
  function formatCents(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Reads cents from a Bundler money node (prefers data-currentprice).
   * @param {Element|null} el
   * @returns {number|null}
   */
  function readCentsFromMoneyEl(el) {
    if (!el) return null;
    const attr = el.getAttribute('data-currentprice') || el.dataset.currentprice;
    if (attr != null && attr !== '') {
      const fromAttr = parseInt(attr, 10);
      if (Number.isFinite(fromAttr)) return fromAttr;
    }
    const raw = String(el.textContent || '').replace(/[^0-9.]/g, '');
    if (!raw) return null;
    return Math.round(parseFloat(raw) * 100);
  }

  /**
   * Resolves the catalog unit price in cents for the current PDP variant.
   * @param {HTMLElement} productInfo
   * @returns {number|null}
   */
  function getRegularUnitPriceCents(productInfo) {
    const fromData = parseInt(productInfo.dataset.regularUnitPrice, 10);
    if (Number.isFinite(fromData) && fromData > 0) return fromData;

    const metaPrice = window.meta && window.meta.product && window.meta.product.variants
      ? window.meta.product.variants[0] && window.meta.product.variants[0].price
      : null;
    if (Number.isFinite(metaPrice) && metaPrice > 0) return metaPrice;

    return null;
  }

  /**
   * Ensures a .bndlr-original-price node exists beside the discounted price.
   * @param {Element} priceWrap
   * @param {Element} discountedEl
   * @returns {HTMLElement}
   */
  function ensureOriginalEl(priceWrap, discountedEl) {
    let el = priceWrap.querySelector('.bndlr-original-price');
    if (el) return el;

    el = document.createElement('span');
    const baseClass = discountedEl.className || '';
    el.className = baseClass.includes('bndlr-discounted-price')
      ? baseClass.replace(/bndlr-discounted-price/g, 'bndlr-original-price')
      : 'bndlr-original-price bndlr-money money';
    el.dataset.tavaCompareManaged = 'true';
    el.setAttribute('data-money-convertible', '');
    priceWrap.appendChild(el);
    return el;
  }

  /**
   * Writes a compare price onto a Bundler money node without duplicating nodes.
   * @param {HTMLElement} el
   * @param {number} cents
   */
  function writeMoneyEl(el, cents) {
    const centsStr = String(cents);
    const formatted = formatCents(cents);
    if (el.getAttribute('data-currentprice') === centsStr && el.textContent === formatted) {
      return;
    }
    el.textContent = formatted;
    el.setAttribute('data-currentprice', centsStr);
    el.dataset.currentprice = centsStr;
    el.setAttribute('data-currency-usd', formatted);
  }

  /**
   * Idempotent PDP update: set each tier's original to regular × qty vs final price.
   */
  function updatePdpPriceDisplays() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    const unitCents = getRegularUnitPriceCents(productInfo);
    if (!unitCents) return;

    const tiers = productInfo.querySelectorAll('.bndlr-radio-container');
    if (!tiers.length) return;

    tiers.forEach((tier) => {
      const qtyInput = tier.querySelector('input[name="bundle_quantity"]');
      const priceWrap = tier.querySelector('.bndlr-price');
      const discountedEl = tier.querySelector('.bndlr-discounted-price');
      if (!qtyInput || !priceWrap || !discountedEl) return;

      const quantity = parseInt(qtyInput.value, 10);
      if (!Number.isFinite(quantity) || quantity < 1) return;

      const originalLineCents = unitCents * quantity;
      const finalCents = readCentsFromMoneyEl(discountedEl);
      if (finalCents == null) return;

      const savings = Math.max(0, originalLineCents - finalCents);
      let originalEl = priceWrap.querySelector('.bndlr-original-price');

      if (savings <= 0) {
        // No discount — hide compare price (remove only nodes we created).
        if (originalEl) {
          if (originalEl.dataset.tavaCompareManaged === 'true') {
            originalEl.remove();
          } else {
            originalEl.hidden = true;
            originalEl.style.display = 'none';
          }
        }
        return;
      }

      if (!originalEl) {
        originalEl = ensureOriginalEl(priceWrap, discountedEl);
      }
      originalEl.hidden = false;
      originalEl.style.removeProperty('display');
      writeMoneyEl(originalEl, originalLineCents);
    });
  }

  /** Debounces display updates so Bundler/Seal can finish mutating the DOM. */
  function scheduleUpdate() {
    if (applying) return;
    clearTimeout(scheduled);
    scheduled = setTimeout(function () {
      applying = true;
      try {
        updatePdpPriceDisplays();
      } finally {
        applying = false;
      }
    }, DEBOUNCE_MS);
  }

  /** Keeps data-regular-unit-price in sync when the shopper changes variants. */
  function bindVariantPriceUpdates(productInfo) {
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
      const variant = evt && evt.data ? evt.data.variant : null;
      if (variant && variant.price != null) {
        productInfo.dataset.regularUnitPrice = String(variant.price);
      }
      scheduleUpdate();
    });
  }

  /** Reacts to Bundler qty + Seal subscription changes. */
  function bindPurchaseOptionEvents(productInfo) {
    productInfo.addEventListener('change', function (evt) {
      const t = evt.target;
      if (!t) return;
      if (
        t.name === 'bundle_quantity' ||
        (t.name && String(t.name).indexOf('subs_type_') === 0) ||
        (t.classList && t.classList.contains('bndlr-subscription-checkbox'))
      ) {
        scheduleUpdate();
      }
    });
    productInfo.addEventListener('click', function (evt) {
      const t = evt.target;
      if (!t || !t.closest) return;
      if (
        t.closest('.bndlr-radio-container') ||
        t.closest('.bndlr-subscription-checkbox') ||
        t.closest('.sealsubs-container')
      ) {
        scheduleUpdate();
      }
    });
  }

  /**
   * Scoped observer: re-apply after Bundler/Seal rewrite price text.
   * Skips while we are writing to avoid mutation loops.
   */
  function observeBundlerPrices(productInfo) {
    if (observer) observer.disconnect();

    observer = new MutationObserver(function () {
      if (applying) return;
      scheduleUpdate();
    });

    const root =
      productInfo.querySelector('.bundler-volume-target-element, .bndlr-automatic-volume-bundle') ||
      productInfo;
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-currentprice', 'data-currency-usd', 'hidden', 'style'],
    });
  }

  /** Boots compare-price correction once the PDP product-info root exists. */
  function init() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    bindVariantPriceUpdates(productInfo);
    bindPurchaseOptionEvents(productInfo);
    observeBundlerPrices(productInfo);
    scheduleUpdate();

    // Bundler often injects after first paint — retry briefly.
    let attempts = 0;
    const bootTimer = setInterval(function () {
      attempts += 1;
      scheduleUpdate();
      if (productInfo.querySelector('.bndlr-radio-container') || attempts >= 40) {
        clearInterval(bootTimer);
      }
    }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging / manual re-run after app updates.
  window.tavaUpdatePdpComparePrices = updatePdpPriceDisplays;
})();
